import React, { useState, useEffect, useCallback } from 'react'; // Added useCallback
import productAPI from '../../services/productApi';
import './AdminManagement.css';

const AdminManagement = ({ setCurrentPage }) => {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [submittedSearch, setSubmittedSearch] = useState('');
  
  const [editingItem, setEditingItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [pagination, setPagination] = useState({});
  const [currentPageNum, setCurrentPageNum] = useState(1);
  
  const [totalCounts, setTotalCounts] = useState({
    products: 0,
    services: 0,
    categories: 0
  });

  // --- FIX: Wrapped in useCallback ---
  const loadProducts = useCallback(async (page = 1) => {
    const response = await productAPI.getProducts({
      page: page,
      per_page: 20,
      search: submittedSearch.trim() || undefined,
      include_inactive: true
    });
    
    if (response && response.success && response.data) {
      setProducts(response.data.products || []);
      setPagination(response.data.pagination || {});
      setTotalCounts(prev => ({
        ...prev,
        products: response.data.pagination?.total || 0
      }));
    } else {
      throw new Error('Failed to load products');
    }
  }, [submittedSearch]); // Dependency for loadProducts

  // --- FIX: Wrapped in useCallback ---
  const loadServices = useCallback(async (page = 1) => {
    const response = await productAPI.getServices({
      page: page,
      per_page: 20,
      search: submittedSearch.trim() || undefined,
      include_inactive: true
    });
    
    if (response && response.success && response.data) {
      setServices(response.data.services || []);
      setPagination(response.data.pagination || {});
      setTotalCounts(prev => ({
        ...prev,
        services: response.data.pagination?.total || 0
      }));
    } else {
      throw new Error('Failed to load services');
    }
  }, [submittedSearch]); // Dependency for loadServices

  // --- FIX: Wrapped in useCallback ---
  const loadCategories = useCallback(async () => {
    const response = await productAPI.getCategories({
      include_counts: true
    });
    
    if (response && response.success && response.data) {
      setCategories(response.data.categories || []);
      setPagination({}); // Categories are not paginated
      setTotalCounts(prev => ({
        ...prev,
        categories: response.data.categories?.length || 0
      }));
    } else {
      throw new Error('Failed to load categories');
    }
  }, []); // No dependencies for loadCategories

  // --- FIX: Wrapped in useCallback ---
  const loadData = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    
    try {
      switch (activeTab) {
        case 'products':
          await loadProducts(page);
          break;
        case 'services':
          await loadServices(page);
          break;
        case 'categories':
          await loadCategories();
          break;
        // --- FIX: Added default case ---
        default:
          console.warn(`Unknown activeTab: ${activeTab}`);
      }
    } catch (err) {
      const errorInfo = productAPI.handleAPIError(err);
      setError(errorInfo.message);
    } finally {
      setLoading(false);
    }
  }, [activeTab, loadProducts, loadServices, loadCategories]); // Dependencies for loadData

  // --- FIX: Updated dependency array ---
  useEffect(() => {
    loadData(currentPageNum);
  }, [loadData, currentPageNum]); // Now correctly depends on loadData and currentPageNum

  const handleSearch = (e) => {
    e.preventDefault();
    setSubmittedSearch(searchTerm);
    if (currentPageNum !== 1) {
      setCurrentPageNum(1);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage !== currentPageNum && newPage >= 1 && newPage <= (pagination.pages || 1)) {
      setCurrentPageNum(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleTabChange = (tab) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
      setSearchTerm('');
      setSubmittedSearch('');
      setCurrentPageNum(1);
    }
  };

  const handleEdit = (item) => {
    setEditingItem({
      ...item,
      type: activeTab === 'products' ? 'product' : activeTab === 'services' ? 'service' : 'category'
    });
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      let response;
      
      switch (activeTab) {
        case 'products':
          response = await productAPI.deleteProduct(id);
          break;
        case 'services':
          response = await productAPI.deleteService(id);
          break;
        case 'categories':
          response = await productAPI.deleteCategory(id);
          break;
        // --- FIX: Added default case ---
        default:
          throw new Error(`Unknown tab for delete: ${activeTab}`);
      }

      if (response && response.success) {
        const currentItems = activeTab === 'products' ? products : 
                              activeTab === 'services' ? services : categories;
        
        if (currentItems.length === 1 && currentPageNum > 1) {
          setCurrentPageNum(currentPageNum - 1);
        } else {
          loadData(currentPageNum);
        }
        setShowDeleteConfirm(null);
      } else {
        throw new Error(response?.message || 'Delete failed');
      }
    } catch (err) {
      const errorInfo = productAPI.handleAPIError(err);
      setError(errorInfo.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async (editedData) => {
    try {
      setLoading(true);
      setError(''); 
      const finalUpdateData = { ...editedData };

      if (editedData.type === 'product' || editedData.type === 'service') {
        let finalImageNames = [...editedData.existingImageNames];
        
        if (editedData.newImageFiles && editedData.newImageFiles.length > 0) {
          const uploadResponse = await productAPI.uploadFiles(editedData.newImageFiles);
          
          if (uploadResponse.success && uploadResponse.data.files) {
            const newNames = uploadResponse.data.files.map(file => file.filename); 
            finalImageNames = [...finalImageNames, ...newNames];
          } else {
            throw new Error(uploadResponse.message || 'Image upload failed');
          }
        }
        
        finalUpdateData.images = finalImageNames; 
        finalUpdateData._original_images = finalImageNames;
        
        delete finalUpdateData.newImageFiles;
        delete finalUpdateData.existingImageNames;
      }

      let response;
      
      switch (finalUpdateData.type) {
        case 'product':
          response = await productAPI.updateProduct(finalUpdateData.id, finalUpdateData);
          break;
        case 'service':
          response = await productAPI.updateService(finalUpdateData.id, finalUpdateData);
          break;
        case 'category':
          response = await productAPI.updateCategory(finalUpdateData.id, finalUpdateData);
          break;
        // --- FIX: Added default case ---
        default:
          throw new Error(`Unknown type for save: ${finalUpdateData.type}`);
      }

      if (response && response.success) {
        loadData(currentPageNum);
        setEditingItem(null);
      } else {
        throw new Error(response?.message || 'Update failed');
      }
    } catch (err) {
      const errorInfo = productAPI.handleAPIError(err);
      setError(errorInfo.message); 
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    const numericPrice = parseFloat(price);
    if (isNaN(numericPrice)) {
      return '$0.00'; 
    }
    return productAPI.formatPrice ? productAPI.formatPrice(numericPrice) : `$${numericPrice.toFixed(2)}`;
  };

  const renderProductRow = (product) => (
    <tr key={product.id} className="data-row">
      <td className="cell-id">{product.id}</td>
      <td className="cell-image">
        {product.images && product.images.length > 0 ? (
          <img 
            src={product.images[0]} 
            alt={product.name}
            className="item-thumbnail"
            onError={(e) => {
              e.target.src = '/api/placeholder/50/50';
            }}
          />
        ) : (
          <div className="no-image">No Image</div>
        )}
      </td>
      <td className="cell-name">
        <div className="item-name">{product.name}</div>
        <div className="item-category">{product.category?.name || 'No Category'}</div>
      </td>
      <td className="cell-price">
        <div className="price-container">
          {product.is_on_sale && product.sale_price ? (
            <>
              <span className="sale-price">{formatPrice(product.sale_price)}</span>
              <span className="original-price">{formatPrice(product.price)}</span>
            </>
          ) : (
            <span className="regular-price">{formatPrice(product.price)}</span>
          )}
        </div>
      </td>
      <td className="cell-stock">
        {product.track_inventory ? (
          <span className={`stock-status ${product.stock_quantity <= 0 ? 'out-of-stock' : product.stock_quantity <= 5 ? 'low-stock' : 'in-stock'}`}>
            {product.stock_quantity}
          </span>
        ) : (
          <span className="no-tracking">Not Tracked</span>
        )}
      </td>
      <td className="cell-status">
        <div className="status-badges">
          <span className={`status-badge ${product.is_active ? 'active' : 'inactive'}`}>
            {product.is_active ? 'Active' : 'Inactive'}
          </span>
          {product.is_featured && <span className="status-badge featured">Featured</span>}
          {product.is_on_sale && <span className="status-badge sale">Sale</span>}
        </div>
      </td>
      <td className="cell-actions">
        <div className="action-buttons">
          <button
            onClick={() => handleEdit(product)}
            className="btn-action edit"
            title="Edit"
          >
            ✏️
          </button>
          <button
            onClick={() => setShowDeleteConfirm({ id: product.id, name: product.name, type: 'product' })}
            className="btn-action delete"
            title="Delete"
          >
            🗑️
          </button>
        </div>
      </td>
    </tr>
  );

  const renderServiceRow = (service) => (
    <tr key={service.id} className="data-row">
      <td className="cell-id">{service.id}</td>
      <td className="cell-image">
        {service.images && service.images.length > 0 ? (
          <img 
            src={service.images[0]} 
            alt={service.name}
            className="item-thumbnail"
            onError={(e) => {
              e.target.src = '/api/placeholder/50/50';
            }}
          />
        ) : (
          <div className="no-image">No Image</div>
        )}
      </td>
      <td className="cell-name">
        <div className="item-name">{service.name}</div>
        <div className="item-category">{service.category?.name || 'No Category'}</div>
      </td>
      <td className="cell-price">
        <div className="price-container">
          {service.is_on_sale && service.sale_price ? (
            <>
              <span className="sale-price">{formatPrice(service.sale_price)}</span>
              <span className="original-price">{formatPrice(service.price)}</span>
            </>
          ) : (
            <span className="regular-price">{formatPrice(service.price)}</span>
          )}
        </div>
        <div className="pricing-model">{service.pricing_model || 'Fixed'}</div>
      </td>
      <td className="cell-availability">
        <span className={`availability-status ${service.is_available ? 'available' : 'unavailable'}`}>
          {service.is_available ? 'Available' : 'Unavailable'}
        </span>
        {service.duration && <div className="duration">Duration: {service.duration}</div>}
      </td>
      <td className="cell-status">
        <div className="status-badges">
          <span className={`status-badge ${service.is_active ? 'active' : 'inactive'}`}>
            {service.is_active ? 'Active' : 'Inactive'}
          </span>
          {service.is_featured && <span className="status-badge featured">Featured</span>}
          {service.is_on_sale && <span className="status-badge sale">Sale</span>}
        </div>
      </td>
      <td className="cell-actions">
        <div className="action-buttons">
          <button
            onClick={() => handleEdit(service)}
            className="btn-action edit"
            title="Edit"
          >
            ✏️
          </button>
          <button
            onClick={() => setShowDeleteConfirm({ id: service.id, name: service.name, type: 'service' })}
            className="btn-action delete"
            title="Delete"
          >
            🗑️
          </button>
        </div>
      </td>
    </tr>
  );

  const renderCategoryRow = (category) => (
    <tr key={category.id} className="data-row">
      <td className="cell-id">{category.id}</td>
      <td className="cell-name">
        <div className="item-name">{category.name}</div>
        {category.description && (
          <div className="item-description">{category.description}</div>
        )}
      </td>
      <td className="cell-counts">
        <div className="count-item">
          Products: {category.product_count || 0}
        </div>
        <div className="count-item">
          Services: {category.service_count || 0}
        </div>
      </td>
      <td className="cell-status">
        <span className={`status-badge ${category.is_active ? 'active' : 'inactive'}`}>
          {category.is_active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="cell-actions">
        <div className="action-buttons">
          <button
            onClick={() => handleEdit(category)}
            className="btn-action edit"
            title="Edit"
          >
            ✏️
          </button>
          <button
            onClick={() => setShowDeleteConfirm({ id: category.id, name: category.name, type: 'category' })}
            className="btn-action delete"
            title="Delete"
            disabled={(category.product_count || 0) > 0 || (category.service_count || 0) > 0}
          >
            🗑️
          </button>
        </div>
      </td>
    </tr>
  );

  const renderPagination = () => {
    if (!pagination || !pagination.pages || pagination.pages <= 1) {
      return null;
    }

    const currentPage = pagination.page || 1; 
    const totalPages = pagination.pages || 1;
    
    let pagesToShow = [1];
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage > 3) {
      pagesToShow.push('...');
    }
    
    for (let i = start; i <= end; i++) {
      if (i !== 1 && i !== totalPages) {
        pagesToShow.push(i);
      }
    }
    
    if (currentPage < totalPages - 2) {
      pagesToShow.push('...');
    }
    
    if (totalPages > 1) {
      pagesToShow.push(totalPages);
    }
    pagesToShow = [...new Set(pagesToShow)]; 

    return (
      <div className="pagination">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-btn"
        >
          Previous
        </button>
        
        <div className="pagination-numbers">
          {pagesToShow.map((page, index) => {
            if (page === '...') {
              return <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>;
            }
            
            return (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`pagination-number ${currentPage === page ? 'active' : ''}`}
              >
                {page}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-btn"
        >
          Next
        </button>

        <div className="pagination-info">
          Page {currentPage} of {totalPages} | Total: {pagination.total || 0} items
        </div>
      </div>
    );
  };


  return (
    <div className="admin-management">
      <div className="admin-header">
        <h1>Manage Inventory</h1>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setCurrentPage('product-admin')}
          >
            Add New Item
          </button>
        </div>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => handleTabChange('products')}
        >
          Products ({totalCounts.products})
        </button>
        <button
          className={`tab ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => handleTabChange('services')}
        >
          Services ({totalCounts.services})
        </button>
        <button
          className={`tab ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => handleTabChange('categories')}
        >
          Categories ({totalCounts.categories})
        </button>
      </div>

      <div className="search-section">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={`Search ${activeTab}...`}
            className="search-input"
            disabled={activeTab === 'categories'}
          />
          <button 
            type="submit" 
            className="search-button"
            disabled={activeTab === 'categories'}
          >
            Search
          </button>
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm(''); 
                setSubmittedSearch(''); 
                if (currentPageNum !== 1) {
                  setCurrentPageNum(1); 
                }
              }}
              className="clear-search"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button onClick={() => setError('')} className="close-error">×</button>
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      )}

      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              {activeTab === 'products' && (
                <>
                  <th>ID</th>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th>Actions</th>
                </>
              )}
              {activeTab === 'services' && (
                <>
                  <th>ID</th>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Price</th>
                  <th>Availability</th>
                  <th>Status</th>
                  <th>Actions</th>
                </>
              )}
              {activeTab === 'categories' && (
                <>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Item Counts</th>
                  <th>Status</th>
                  <th>Actions</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {activeTab === 'products' && products.map(renderProductRow)}
            {activeTab === 'services' && services.map(renderServiceRow)}
            {activeTab === 'categories' && categories.map(renderCategoryRow)}
            
            {!loading && (
              (activeTab === 'products' && products.length === 0) ||
              (activeTab === 'services' && services.length === 0) ||
              (activeTab === 'categories' && categories.length === 0)
            ) && (
              <tr>
                <td colSpan="7" className="empty-state">
                  <div className="empty-content">
                    <h3>No {activeTab} found</h3>
                    <p>
                      {submittedSearch 
                        ? `No ${activeTab} match your search "${submittedSearch}"`
                        : `No ${activeTab} have been added yet`
                      }
                    </p>
                    <button 
                      className="btn btn-primary"
                      onClick={() => setCurrentPage('product-admin')}
                    >
                      Add First {activeTab.slice(0, -1)}
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {renderPagination()}

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Delete</h3>
            <p>
              Are you sure you want to delete this {showDeleteConfirm.type}?
            </p>
            <div className="item-preview">
              <strong>{showDeleteConfirm.name}</strong>
            </div>
            {showDeleteConfirm.type === 'category' && 
             ((categories.find(c => c.id === showDeleteConfirm.id)?.product_count || 0) > 0 ||
              (categories.find(c => c.id === showDeleteConfirm.id)?.service_count || 0) > 0) && (
              <div className="warning-message">
                ⚠️ This category contains items and cannot be deleted.
              </div>
            )}
            <div className="modal-actions">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm.id)}
                className="btn btn-danger"
                disabled={showDeleteConfirm.type === 'category' && 
                          ((categories.find(c => c.id === showDeleteConfirm.id)?.product_count || 0) > 0 ||
                           (categories.find(c => c.id === showDeleteConfirm.id)?.service_count || 0) > 0)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {editingItem && (
        <EditModal
          item={editingItem}
          categories={categories}
          onSave={handleSaveEdit}
          onCancel={() => setEditingItem(null)}
          loading={loading}
        />
      )}
    </div>
  );
};

// --- EditModal Component ---

const EditModal = ({ item, categories, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    ...item,
    features: item.features || [],
    tags: item.tags || []
  });

  const [newImageFiles, setNewImageFiles] = useState([]);
  const [currentImageNames, setCurrentImageNames] = useState(item._original_images || []);


  const handleSubmit = (e) => {
    e.preventDefault();
    
    const cleanedData = {
      id: formData.id,
      type: item.type,
      name: formData.name?.trim() || '',
      is_active: formData.is_active ? 1 : 0, 
    };
    
    if (item.type !== 'category') {
      cleanedData.description = formData.description?.trim() || '';
      cleanedData.category_id = formData.category_id ? parseInt(formData.category_id) : null;
      cleanedData.price = formData.price ? parseFloat(formData.price) : 0;
      cleanedData.is_featured = formData.is_featured ? 1 : 0;
      cleanedData.is_on_sale = formData.is_on_sale ? 1 : 0;
      
      if (formData.is_on_sale && formData.sale_price) {
        cleanedData.sale_price = parseFloat(formData.sale_price);
      } else {
        cleanedData.sale_price = null;
      }
      
      cleanedData.newImageFiles = newImageFiles;
      cleanedData.existingImageNames = currentImageNames;

      delete cleanedData.images;
      delete cleanedData._original_images;
      delete cleanedData.tags;
      delete cleanedData.features;
    }
    
    if (item.type === 'product') {
      cleanedData.stock_quantity = formData.stock_quantity ? parseInt(formData.stock_quantity) : 0;
      cleanedData.track_inventory = formData.track_inventory ? 1 : 0;
    }
    
    if (item.type === 'service') {
      cleanedData.pricing_model = formData.pricing_model || 'fixed';
      cleanedData.is_available = formData.is_available ? 1 : 0;
      if (formData.duration) {
        cleanedData.duration = formData.duration.trim() || null;
      }
    }
    
    if (item.type === 'category') {
      cleanedData.description = formData.description?.trim() || '';
    }
    
    onSave(cleanedData);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileSelect = (e) => {
    setNewImageFiles(Array.from(e.target.files));
  };

  const handleRemoveCurrentImage = (imageName) => {
    setCurrentImageNames(prev => prev.filter(name => name !== imageName));
  };

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      is_active: !!prev.is_active,
      is_featured: !!prev.is_featured,
      is_on_sale: !!prev.is_on_sale,
      track_inventory: !!prev.track_inventory,
      is_available: !!prev.is_available
    }));
    setCurrentImageNames(item._original_images || []);
  }, [item]);

  return (
    <div className="modal-overlay">
      <div className="modal-content edit-modal">
        <h3>Edit {item.type}</h3>
        <form onSubmit={handleSubmit} className="edit-form">
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>

          {item.type !== 'category' && (
            <>
              <div className="form-group">
                <label>Current Images</label>
                <div className="image-preview-container">
                  {currentImageNames.length > 0 ? (
                    currentImageNames.map((imageName, index) => (
                      <div key={index} className="image-preview-item">
                        <img 
                          src={productAPI.getImageUrl(imageName)} 
                          alt="Existing preview" 
                          className="item-thumbnail"
                        />
                        <button 
                          type="button" 
                          onClick={() => handleRemoveCurrentImage(imageName)}
                          className="btn-remove-image"
                          title={`Remove ${imageName}`}
                        >
                          &times;
                        </button>
                      </div>
                    ))
                  ) : (
                    <p>No images.</p>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Add New Images</label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                />
                {newImageFiles.length > 0 && (
                  <div className="image-preview-container new-previews">
                    <p>{newImageFiles.length} new file(s) selected:</p>
                    {newImageFiles.map((file, index) => (
                      <span key={index} className="new-file-name">{file.name}</span>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Category</label>
                <select
                  name="category_id"
                  value={formData.category_id || ''}
                  onChange={handleInputChange}
                >
                  <option value="">No Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Price</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleInputChange}
                  />
                  Featured
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="is_on_sale"
                    checked={formData.is_on_sale}
                    onChange={handleInputChange}
                  />
                  On Sale
                </label>
              </div>

              {formData.is_on_sale && (
                <div className="form-group">
                  <label>Sale Price</label>
                  <input
                    type="number"
                    name="sale_price"
                    value={formData.sale_price || ''}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                  />
                </div>
              )}

              {item.type === 'product' && (
                <>
                  <div className="form-group">
                    <label>Stock Quantity</label>
                    <input
                      type="number"
                      name="stock_quantity"
                      value={formData.stock_quantity || ''}
                      onChange={handleInputChange}
                      min="0"
                      disabled={!formData.track_inventory}
                    />
                  </div>
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="track_inventory"
                        checked={formData.track_inventory}
                        onChange={handleInputChange}
                      />
                      Track Inventory
                    </label>
                  </div>
                </>
              )}
              
              {item.type === 'service' && (
                 <>
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="is_available"
                        checked={formData.is_available}
                        onChange={handleInputChange}
                      />
                      Is Available
                    </label>
                  </div>
                  <div className="form-group">
                    <label>Duration (e.g., "30 min", "1 hour")</label>
                    <input
                      type="text"
                      name="duration"
                      value={formData.duration || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                   <div className="form-group">
                    <label>Pricing Model</label>
                    <select
                      name="pricing_model"
                      value={formData.pricing_model || 'fixed'}
                      onChange={handleInputChange}
                    >
                      <option value="fixed">Fixed</option>
                      <option value="hourly">Hourly</option>
                      <option value="per_session">Per Session</option>
                    </select>
                  </div>
                </>
              )}
            </>
          )}

          {item.type === 'category' && (
            <>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                  rows={2}
                />
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
              </div>
            </>
          )}

          <div className="modal-actions">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminManagement;